import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  BookOpenText,
  LayoutDashboard,
  Store,
  Ticket,
  UserRound,
  Users,
} from 'lucide-react'
import { Logo } from '@/components/Logo'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth'
import { hasAtLeastRole } from '@shared/model'
import { NoticeBar } from '@/components/NoticeBar'

interface NavItem {
  to: string
  label: string
  icon: typeof BookOpenText
  end?: boolean
}

const BASE_ITEMS: NavItem[] = [
  { to: '/', label: 'Häftet', icon: BookOpenText, end: true },
  { to: '/foretag', label: 'Företag', icon: Store },
  { to: '/mina-koder', label: 'Mina koder', icon: Ticket },
  { to: '/konto', label: 'Mitt konto', icon: UserRound },
]

export function AppLayout() {
  const { role } = useAuth()
  const location = useLocation()

  const items = [...BASE_ITEMS]
  if (hasAtLeastRole(role, 'teamLeader')) {
    items.splice(3, 0, { to: '/lag', label: 'Mitt lag', icon: Users })
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="sticky top-0 z-30 bg-brand-surface pt-safe text-on-brand shadow-card">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-3 px-4">
          <NavLink to="/" aria-label="Till häftet">
            <Logo size={34} />
          </NavLink>

          {/* På desktop ligger navigeringen i sidhuvudet i stället för längst ned. */}
          <nav aria-label="Huvudmeny" className="hidden sm:block">
            <ul className="flex items-center gap-1">
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        'flex h-10 items-center gap-2 rounded-pill px-3.5 text-sm font-semibold transition-colors',
                        isActive
                          ? 'bg-accent text-on-accent'
                          : 'text-on-brand/80 hover:bg-white/10 hover:text-on-brand',
                      )
                    }
                  >
                    <item.icon size={18} aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              ))}
              {hasAtLeastRole(role, 'admin') && (
                <li>
                  <NavLink
                    to="/admin"
                    className="flex h-10 items-center gap-2 rounded-pill px-3.5 text-sm font-semibold text-on-brand/80 transition-colors hover:bg-white/10 hover:text-on-brand"
                  >
                    <LayoutDashboard size={18} aria-hidden />
                    Admin
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </header>

      <NoticeBar />

      <main
        // Nyckeln gör att sidan börjar överst vid varje navigering i stället
        // för att behålla föregående sidas rullningsläge.
        key={location.pathname}
        className="mx-auto w-full max-w-5xl flex-1 px-4 pt-4 pb-28 sm:pb-10"
      >
        <Outlet />
      </main>

      {/* Mobilnavigering. Ligger kvar längst ned där tummen når. */}
      <nav
        aria-label="Huvudmeny"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface pb-safe sm:hidden"
      >
        <ul className="flex items-stretch justify-around">
          {items.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-16 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold transition-colors',
                    isActive ? 'text-brand-ink' : 'text-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-7 w-12 items-center justify-center rounded-pill transition-colors',
                        isActive && 'bg-brand-soft',
                      )}
                    >
                      <item.icon size={20} aria-hidden />
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
